import React, { useEffect, useCallback } from 'react';
import { AppState } from '$/store';
import HeadBlock from '$/components/global/heading';
import { connect } from 'react-redux';
import HeadTab from '$/components/global/headingTab';
import { Routes, Route, useMatch } from 'react-router-dom';
import CloIndustries from './CloIndustries';
import CloComps from './CloComps';
import CloOverview from './CloOverview';
import * as Bixby from '$/core';
import { getCloDealById, setHeatMapActive } from '$/store/cloDeal/actions';
import NotFound from '$/pages/not-found';
import { IToggle } from '$/components/global/heading';
import { getSFDREnabled, getTreppCLOEnabled } from '$/store/release-feature-flags/selectors';
import { downloadFile } from '$/store/export/actions';
import { toastError } from '$/store/toast/actions';
import { CloService } from '$/services';

interface ICloDealDetailsProps {
  isLoading: boolean;
  featureFlags?: string[];
  currentClo?: Bixby.Models.CloDeal;
  dispatchGetCurrentClo: (id: string) => Promise<void>;
  dispatchSetHeatMap: (isActive: boolean) => boolean;
  heatMapActive: boolean;
  isTreppCLOEnabled: boolean;
  isSFDREnabled: boolean;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
  dispatchDownloadFailure: () => Promise<void>;
}

const CloDealDetails: React.FC<ICloDealDetailsProps> = props => {
  const {
    currentClo,
    dispatchGetCurrentClo,
    isLoading,
    heatMapActive,
    dispatchSetHeatMap,
    isTreppCLOEnabled,
    isSFDREnabled,
    dispatchDownloadDocument,
    dispatchDownloadFailure,
  } = props;
  const featureFlags: string[] = props.featureFlags || [];
  const match = useMatch('/clodeal-analytics/:cloId/*');

  const heatMapToggle: IToggle | undefined = {
    checked: heatMapActive,
    onChange: (isHeatMapOn: boolean) => {
      if (isHeatMapOn) {
        dispatchSetHeatMap(false);
      } else {
        dispatchSetHeatMap(true);
      }
    },
  };

  const showPaiReportLink =
    featureFlags?.includes(Bixby.Constants.CLIENT_SFDR_VIEW) && isSFDREnabled && isTreppCLOEnabled;

  const downloadSFDRReport = useCallback(async () => {
    try {
      if (!currentClo) return;
      const { blob, filename } = await CloService.downloadSFDRReportById(currentClo.id);

      dispatchDownloadDocument(blob, filename ?? `${currentClo.id}.pdf`);
    } catch {
      dispatchDownloadFailure();
    }
  }, [dispatchDownloadDocument, dispatchDownloadFailure, currentClo]);

  useEffect(() => {
    if (match?.params.cloId && match?.params.cloId !== currentClo?.id) {
      const setClo = match.params.cloId;
      dispatchGetCurrentClo(setClo);
    }
  }, [currentClo, dispatchGetCurrentClo, isLoading, match]);

  const baseUrl = `/clodeal-analytics/${match?.params.cloId}`;
  const industriesUrl = `${baseUrl}/industries`;
  const compsUrl = `${baseUrl}/comps`;
  if (!featureFlags?.includes(Bixby.Constants.CLIENT_CLO_VIEW)) {
    return <NotFound />;
  } else {
    return currentClo && match && currentClo.id === match.params.cloId ? (
      <React.Fragment>
        <HeadBlock
          title={currentClo.name}
          heatMapToggle={heatMapToggle}
          exportButtonLabel="SFDR Report"
          handleExport={downloadSFDRReport}
          exportEnabled={showPaiReportLink}>
          <HeadTab title="Overview" link={baseUrl} />
          <HeadTab title="Industries" link={industriesUrl} exact={true} />
          <HeadTab title="Comps" link={compsUrl} exact={true} />
        </HeadBlock>
        <div>
          <Routes>
            <Route path={'/*'} element={<CloOverview />} />
            <Route path={'/industries'} element={<CloIndustries />} />
            <Route path={'/comps'} element={<CloComps />} />
          </Routes>
        </div>
      </React.Fragment>
    ) : null;
  }
};

const mapStateToProps = (state: AppState) => {
  const { isTreppCLOEnabled } = getTreppCLOEnabled(state);
  const { isSFDREnabled } = getSFDREnabled(state);

  return {
    isLoading: state.cloDealState.isLoading,
    currentClo: state.cloDealState.currentCloDeal,
    featureFlags: state.loginState.featureFlags,
    heatMapActive: state.cloDealState.heatMapActive,
    isTreppCLOEnabled,
    isSFDREnabled,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCurrentClo: (id: string) => dispatch(getCloDealById(id)),
  dispatchSetHeatMap: (isActive: boolean) => dispatch(setHeatMapActive(isActive)),
  dispatchDownloadDocument: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchDownloadFailure: () => dispatch(toastError('Failed to download report')),
});
export default connect(mapStateToProps, mapDispatchToProps)(CloDealDetails);
